import { CategoryResponse, getCategoriesByProducts, getSecondCategoriesAllByProducts } from "@/js/repository/CategoryRepository";
import { getCategoryPath, getProductPath } from "@/js/utils/stringUtils";
import { GetServerSideProps } from "next";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import RatingStar from "@/components/app/RatingStar";
import { Separator } from "@/components/ui/separator";
import { convertCurrencySign, formatSalesQuantity } from "@/js/format/Format";
import { ProductResponse } from "@/js/repository/ProductRepository";
import { TicketPercent } from "lucide-react";

const truncateTitle = (title: string, maxLength: number = 70) =>
  title.length > maxLength ? title.substring(0, maxLength) + '...' : title;

/**
 * 기존 URL /products?category=1 호환: /category/1-name 으로 301 리다이렉트
 */
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { category, page } = context.query;
  if (!category || typeof category !== 'string') {
    return { redirect: { destination: '/', permanent: true } };
  }

  const locale = context.locale ?? 'ko';
  const categories = await getCategoriesByProducts(locale);
  const secondCategoriesAll = await getSecondCategoriesAllByProducts(locale);
  const categoryId = parseInt(category, 10);
  if (Number.isNaN(categoryId)) {
    return { redirect: { destination: '/', permanent: true } };
  }

  const categoryInfo = categories.find((c: CategoryResponse) => c.id === categoryId)
    ?? secondCategoriesAll.find((c: CategoryResponse) => c.id === categoryId);
  if (!categoryInfo) {
    return { redirect: { destination: '/', permanent: true } };
  }

  const dest = getCategoryPath(categoryId, categoryInfo.name);
  const pageNum = parseInt(String(page), 10);
  const destination = pageNum > 1 ? `${dest}?page=${pageNum}` : dest;
  return {
    redirect: {
      destination,
      permanent: true,
    },
  };
};

export default function ProductsRedirect() {
  return null;
}

export function ProductList(props: any) {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const products: ProductResponse[] = props.products;

  return (
    <ul className="grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      { products.map((product: ProductResponse) => {
        const sale_price = parseFloat(product.detail.target_sale_price);
        const original_price = parseFloat(product.detail.target_original_price);
        const discount_rate = original_price > 0 ? Math.max(0, ((original_price - sale_price) / original_price) * 100) : 0;
        const rating = product.detail.evaluate_rate ? (parseInt(product.detail.evaluate_rate.replace('%', ''))/20).toFixed(1) : null;
        const promoCode = product.detail.promo_code_info?.promo_code?.trim();

        return (
          <li className="group overflow-hidden rounded-lg border border-black/10 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-[#fd384f]/40 hover:shadow-lg" key={product.detail.product_id}>
            <a className="block h-full text-[0.9em]" href={getProductPath(product.detail.product_id, product.detail.product_title)} aria-label={`${product.detail.product_title} ${t("openDeal")}`}>
              { product.detail.product_main_image_url && 
                  <div className="relative aspect-square bg-[#f2efe8]">
                    <div className="absolute left-2 top-2 z-10 flex max-w-[calc(100%-1rem)] flex-wrap gap-1.5" aria-hidden="true">
                      {discount_rate > 0 && (
                        <span className="rounded-md bg-[#fd384f] px-2 py-1 text-[0.72rem] font-black text-white">-{discount_rate.toFixed(0)}%</span>
                      )}
                      {promoCode && (
                        <span className="inline-flex max-w-[132px] items-center gap-1 rounded-md bg-[#101820] px-2 py-1 text-[0.72rem] font-black text-white shadow-sm">
                          <TicketPercent className="h-3 w-3 shrink-0" />
                          <span className="truncate">쿠폰 {promoCode}</span>
                        </span>
                      )}
                    </div>
                    <img className="h-full w-full object-contain p-2 transition duration-300 group-hover:scale-[1.03]" src={product.detail.product_main_image_url} alt={`${truncateTitle(product.detail.product_title)}`} width={600} height={600} loading="lazy"/>
                  </div>
              }
              <div className="flex flex-col p-3">
                <span className="line-clamp-2 min-h-[40px] break-words text-sm font-semibold leading-5 text-gray-950 group-hover:text-[#fd384f]">{product.detail.product_title}</span>
                <div className="mt-2 flex min-h-[20px] items-center text-[0.75rem] text-gray-500">
                  { rating &&
                    <RatingStar rating={ Number(rating) } />
                  }
                  {rating && <div className="ml-1">{rating}</div>}
                  <Separator className="mx-2 h-[12px] w-[1px] border-l border-gray-300"/>
                  <div className="truncate">{ formatSalesQuantity(product.detail.lastest_volume, locale) } {t("sold")}</div>
                </div>

                <div className="mt-3 flex min-h-[42px] flex-wrap items-end gap-x-2 gap-y-1">
                  <span className="text-lg font-black leading-none text-[#fd384f]">{ convertCurrencySign(product.detail.target_sale_price_currency, sale_price.toLocaleString(locale)) }</span>
                  {original_price > sale_price && (
                    <span className="text-xs font-medium leading-none text-gray-400 line-through">{ convertCurrencySign(product.detail.target_sale_price_currency, original_price.toLocaleString(locale)) }</span>
                  )}
                </div>
                {promoCode && (
                  <div className="mt-2 inline-flex min-h-7 max-w-full items-center gap-1.5 self-start rounded-md border border-[#fd384f]/20 bg-[#fff1f2] px-2 py-1 text-xs font-black text-[#b91c1c]">
                    <TicketPercent className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">프로모션 코드 {promoCode}</span>
                  </div>
                )}
                <span className="mt-3 flex h-9 items-center justify-center rounded-lg bg-[#101820] text-xs font-bold text-white transition group-hover:bg-[#fd384f]">{t("openDeal")}</span>
              </div>

              { process.env.NODE_ENV !== 'production' &&
                <div className="text-xs text-gray-500 mt-1">
                  score: { product.admin_data?.score }<br/>
                  discountNum: { product.admin_data?.discountNum }<br/>
                  commissionRateNum: { product.admin_data?.commissionRateNum }<br/>
                  evaluateRateNum: { product.admin_data?.evaluateRateNum }<br/>
                  latestVolumeNum: { product.admin_data?.latestVolumeNum }<br/>
                  priceScore: { product.admin_data?.priceScore }<br/>
                </div>
              }
            </a>

          </li>
        );
      })}
    </ul>
  );
}
